import type { Control, UseFormSetValue } from "react-hook-form";
import { Loader2 } from "lucide-react";
import type { PDFPreviewData, ScopeOfWorkData } from "@rebuild/shared";
import { PDFHeaderDisplay, PDFHeaderInfoEditor, PDFScopeOfWorkEditor, PDFTotalCostEditor, PDFTermsFooterEditor } from "./editors";
import type { PDFPreviewFormData } from "./pdfPreview.types";
import { useInlineEditing } from "./hooks/useInlineEditing";

interface PDFPreviewDocumentProps {
  previewData: PDFPreviewData;
  control: Control<PDFPreviewFormData>;
  setValue: UseFormSetValue<PDFPreviewFormData>;
  editableScopeOfWork: ScopeOfWorkData | null;
  setEditableScopeOfWork: (data: ScopeOfWorkData) => void;
  // Calculated values for display
  overhead: string;
  profit: string;
  taxAmount: string;
  total: string;
  // Flags
  isSubtotalEditable: boolean;
  isTotalEditable: boolean;
  includeTax: boolean;
  includeOverheadAndProfit: boolean;
  overheadPercent: number;
  profitPercent: number;
  includeQuantity: boolean;
  isReloading?: boolean;
}

export function PDFPreviewDocument({
  previewData,
  control,
  setValue,
  editableScopeOfWork,
  setEditableScopeOfWork,
  overhead,
  profit,
  taxAmount,
  total,
  isSubtotalEditable,
  isTotalEditable,
  includeTax,
  includeOverheadAndProfit,
  overheadPercent,
  profitPercent,
  includeQuantity,
  isReloading = false,
}: PDFPreviewDocumentProps) {
  const inlineEditing = useInlineEditing({
    editableScopeOfWork,
    setEditableScopeOfWork,
    onSubtotalChange: (subtotal) => setValue("subtotal", subtotal),
    onScopeNoteTextChange: (text) => setValue("scopeNoteText", text),
  });

  return (
    <div className="relative flex-2 overflow-y-auto rounded-lg border border-gray-300 bg-gray-50 px-10 py-6">
      {isReloading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-white/80">
          <div className="flex flex-col items-center">
            <Loader2 className="mb-2 h-8 w-8 animate-spin text-blue-600" />
            <span className="text-sm text-gray-600">Resetting preview...</span>
          </div>
        </div>
      )}
      <div className="mb-6 overflow-hidden rounded-lg bg-white shadow-lg">
        <PDFHeaderDisplay
          topLeftText={previewData.topLeftText}
          bannerImageUrl={previewData.bannerImageUrl}
          bannerImageScale={previewData.bannerImageScale}
          bannerImageAlignment={previewData.bannerImageAlignment}
        />

        {/* Proposal Content */}
        <div className={`px-8 ${!previewData.footerImageUrl ? "pb-6" : ""}`}>
          <PDFHeaderInfoEditor control={control} />

          <PDFScopeOfWorkEditor
            control={control}
            editableScopeOfWork={editableScopeOfWork}
            inlineEditing={inlineEditing}
            includeQuantity={includeQuantity}
          />

          <PDFTotalCostEditor
            control={control}
            previewDataOverhead={previewData?.overhead}
            previewDataProfit={previewData?.profit}
            overhead={overhead}
            profit={profit}
            taxAmount={taxAmount}
            total={total}
            isSubtotalEditable={isSubtotalEditable}
            isTotalEditable={isTotalEditable}
            includeTax={includeTax}
            includeOverheadAndProfit={includeOverheadAndProfit}
            overheadPercent={overheadPercent}
            profitPercent={profitPercent}
          />

          <PDFTermsFooterEditor control={control} />
        </div>

        {/* Footer Image */}
        {previewData.footerImageUrl && (
          <div className="px-8 pb-6">
            {/* Matches the PDF's pdfStyle.footerImage.maxHeight cap */}
            <img src={previewData.footerImageUrl} alt="Footer" className="h-auto max-h-[100px] w-full object-contain object-left" />
          </div>
        )}
      </div>
    </div>
  );
}
